import { useEffect, useRef, useState, useCallback, forwardRef } from 'react';
import { Alert } from '@dreamtree-org/twreact-ui';
import { Maximize, Minimize } from 'lucide-react';
import { framedBy } from '@/lib/framedBy';

/**
 * The video element, plus everything that makes a copy of it attributable.
 *
 * ⚠️ READ THIS BEFORE CHANGING ANYTHING HERE.
 *
 * None of this prevents screen recording. Nothing a web page can do prevents screen
 * recording — there is no API to detect it, and a phone pointed at the monitor defeats
 * even hardware DRM. What this file does is make a recording (a) carry the viewer's own
 * identity in the picture and (b) awkward to produce without that identity in it.
 *
 * The layers, in descending order of how much they actually matter:
 *
 *   1. The authenticated stream route (routes/video.js) — the only thing stopping the FILE
 *      being taken. Everything below is about recordings, not downloads.
 *   2. The watermark + the integrity check that pauses playback when it is removed. This
 *      closes the realistic clean-capture path: open DevTools, delete the overlay, record.
 *   3. controlsList / no-PiP / no-context-menu — removes the DISCOVERABLE save routes.
 *      Cosmetic against anyone with DevTools open.
 *
 * @param {{ playback: {url,type,sessionCode,viewerLabel}|null, onRefresh?: () => Promise<object|null>,
 *           poster?: string, autoPlay?: boolean, className?: string }} props
 */
export default function VideoPlayer({ playback, onRefresh, poster, autoPlay = false, className = '' }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const markARef = useRef(null);
  const markBRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [tampered, setTampered] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const recovering = useRef(false);

  const src = playback && playback.url;
  const code = playback && playback.sessionCode;
  const label = (playback && playback.viewerLabel) || null;

  const handleError = async () => {
    if (recovering.current || !onRefresh) { setFailed(true); return; }
    recovering.current = true;
    const next = await onRefresh();
    if (next && ref.current) ref.current.src = next.url;
    else setFailed(true);
  };

  useEffect(() => { recovering.current = false; setFailed(false); }, [src]);

  // --- watermark integrity ---------------------------------------------------------
  //
  // A periodic check of the INVARIANT, not a MutationObserver reacting to changes. React
  // re-renders this subtree constantly (the marks move, the clock ticks), so an observer
  // that paused on any mutation would fight the framework and false-positive on ordinary
  // updates. Asserting "the marks are still there and still visible" is stable under
  // re-render and catches exactly the thing worth catching.
  //
  // Honest ceiling: this stops someone deleting the overlay in DevTools and recording
  // clean. It does not stop disabling JavaScript, patching the bundle, or proxying the
  // page. It moves the attack from ten seconds to actually understanding the app.
  const marksIntact = useCallback(() => {
    if (!code) return true; // nothing to protect yet
    // The token BOTH marks always render. Mark A pairs it with the clock and mark B with
    // the session code, so asserting the code itself would false-positive on mark A —
    // which it did, pausing every legitimate playback until this was caught in a browser.
    const token = label || code;
    for (const r of [markARef, markBRef]) {
      const el = r.current;
      if (!el || !el.isConnected) return false;

      // checkVisibility() is the platform's own answer to "is this actually shown", and it
      // accounts for display, visibility, opacity and content-visibility together —
      // including values applied from a stylesheet, which hand-rolled getComputedStyle
      // checks kept getting wrong. Feature-detected because it is relatively new
      // (Chrome 105+, Safari 17.4+); older browsers fall back to the manual checks below.
      if (typeof el.checkVisibility === 'function') {
        if (!el.checkVisibility({ opacityProperty: true, visibilityProperty: true, contentVisibilityAuto: true })) {
          return false;
        }
      } else {
        const cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none') return false;
        if (Number.parseFloat(cs.opacity) < 0.08) return false;
      }

      // Size and text are checked regardless — checkVisibility() reports a 0px-font or
      // emptied span as perfectly visible, and both would produce an unreadable mark.
      const box = el.getBoundingClientRect();
      if (box.width < 8 || box.height < 4) return false;
      if (!el.textContent || !el.textContent.includes(token)) return false;
    }
    return true;
  }, [code, label]);

  useEffect(() => {
    if (!code) return undefined;
    const check = () => {
      const el = ref.current;
      if (!el) return;
      if (marksIntact()) { setTampered(false); return; }
      setTampered(true);
      if (!el.paused) el.pause();
    };
    const t = setInterval(check, 2000);
    const el = ref.current;
    if (el) el.addEventListener('play', check);
    return () => {
      clearInterval(t);
      if (el) el.removeEventListener('play', check);
    };
  }, [code, marksIntact]);

  // --- fullscreen ------------------------------------------------------------------
  //
  // The browser's own fullscreen button fullscreens the <video> ELEMENT, and the overlay is
  // a SIBLING of it — so native fullscreen shows the picture with no watermark on it. The
  // element cannot contain the marks (a <video> renders no children), so the only fix is to
  // fullscreen the wrapper instead: `nofullscreen` removes the browser's button and the one
  // below takes video and overlay up together.
  useEffect(() => {
    const onChange = () => {
      const fs = document.fullscreenElement;
      // `nofullscreen` in controlsList is set and Chrome reports it as supported, yet the
      // native button still renders in current builds — verified in a browser, not assumed.
      // So don't rely on hiding it: if the VIDEO ends up fullscreen, hand fullscreen over
      // to the wrapper instead. The overlay is a sibling of <video> and a <video> renders
      // no children, so video-element fullscreen shows the picture with no watermark on it.
      if (fs && fs === ref.current && wrapRef.current) {
        document.exitFullscreen()
          .then(() => wrapRef.current && wrapRef.current.requestFullscreen())
          .catch(() => { /* browser refused — nothing further to do */ });
        return;
      }
      setIsFull(fs === wrapRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (wrapRef.current) await wrapRef.current.requestFullscreen();
    } catch { /* denied by the browser or unsupported — leave the player as it is */ }
  };

  // --- watch-time heartbeat ---------------------------------------------------------
  useEffect(() => {
    if (!src) return undefined;
    const t = setInterval(() => {
      const el = ref.current;
      if (!el || el.paused || !Number.isFinite(el.currentTime)) return;
      // Fire-and-forget: an audit beacon must never interfere with playback.
      fetch(src.replace(/\/stream$/, '/heartbeat'), {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        // framedBy rides along so the paths with no unlock gate (unlisted links, a
        // signed-in preview) still get the framing page recorded once.
        body: JSON.stringify({ watchedSecs: Math.round(el.currentTime), framedBy: framedBy() }),
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [src]);

  if (failed) {
    return (
      <Alert variant="error" title="This video could not be played">
        The video could not be reached, or your access has expired. Reload the page to try again.
      </Alert>
    );
  }

  return (
    <div ref={wrapRef} className="relative w-full h-full bg-black">
      <video
        ref={ref}
        src={src || undefined}
        poster={poster || undefined}
        controls
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
        onError={handleError}
        // nofullscreen: the browser's own button would fullscreen the <video> and leave the
        // watermark behind. See the fullscreen note above.
        controlsList="nodownload noremoteplayback nofullscreen"
        disablePictureInPicture
        disableRemotePlayback
        onContextMenu={(e) => e.preventDefault()}
        className={`w-full h-full bg-black ${className}`}
      />

      <Watermark ref={markARef} code={code} label={label} showClock periodMs={7000} seed={0} />
      <Watermark ref={markBRef} code={code} label={label} periodMs={11000} seed={1} />

      {code ? <StartBanner label={label} code={code} /> : null}

      {/* pointer-events-auto inside an otherwise inert overlay layer. */}
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={isFull ? 'Exit full screen' : 'Full screen'}
        // Top-right, not bottom-right: the native control bar owns the bottom edge and a
        // button there is half-hidden behind it.
        className="absolute top-2 right-2 z-20 rounded bg-black/50 p-1.5 text-white/80 hover:text-white hover:bg-black/70 pointer-events-auto"
      >
        {isFull ? <Minimize size={18} /> : <Maximize size={18} />}
      </button>

      {tampered ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/85 p-6 text-center">
          <p className="text-white text-sm max-w-sm">
            Playback stopped because the viewing watermark was removed. Reload the page to
            continue watching.
          </p>
        </div>
      ) : null}
    </div>
  );
}

const clockNow = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** 0..1 from the CSPRNG. A predictable walk would make the next position guessable, and so
 *  the mark easier to crop around automatically. */
function rand() {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] / 2 ** 32;
}

/**
 * One drifting identity mark. Two are rendered, with coprime periods (7s / 11s) so they
 * never settle into a synchronised pattern a cropper could plan around, and offset starting
 * quadrants so they don't open life on top of each other.
 *
 * Repositioned in JS rather than by CSS animation on purpose: index.css disables all
 * animation under prefers-reduced-motion, which would freeze a CSS drift into exactly the
 * static corner mark that is trivial to crop out.
 */
const Watermark = forwardRef(function WatermarkImpl({ code, label, showClock = false, periodMs = 8000, seed = 0 }, ref) {
  const [clock, setClock] = useState(clockNow);
  const [pos, setPos] = useState(() => (seed ? { top: 58, left: 46 } : { top: 16, left: 10 }));

  useEffect(() => {
    const move = () => setPos({ top: 8 + rand() * 62, left: 8 + rand() * 58 });
    const t = setInterval(() => { move(); setClock(clockNow()); }, periodMs);
    return () => clearInterval(t);
  }, [periodMs]);

  if (!code) return null;

  // The identity is the deterrent; the code is what ties a frame to an audit row. Each mark
  // carries the identity, so cropping one still leaves an attributable frame. The two marks
  // differ in their second field (clock vs code) so one frame shows both.
  //
  // With no identity (an unlisted link) the code IS the whole label, so mark B would read
  // "CODE · CODE" if it took the same shape. Fall back to the bare code instead.
  let text;
  if (label) text = `${label} · ${showClock ? clock : code}`;
  else text = showClock ? `${code} · ${clock}` : code;

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden select-none z-10" aria-hidden="true">
      <span
        ref={ref}
        className="absolute whitespace-nowrap font-mono text-white/30 text-sm tracking-widest transition-all duration-1000"
        style={{ top: `${pos.top}%`, left: `${pos.left}%`, textShadow: '0 1px 3px rgba(0,0,0,.8)' }}
      >
        {text}
      </span>
    </div>
  );
});

/** A louder mark for the first few seconds, so the viewer is in no doubt the copy is
 *  traced. Fades out rather than lingering — the drifting marks carry it from there. */
function StartBanner({ label, code }) {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShow(false), 5000);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-3" aria-hidden="true">
      <span className="rounded bg-black/55 px-3 py-1 font-mono text-xs tracking-wide text-white/75">
        {label ? `${label} · ` : ''}{code} · this viewing is watermarked and recorded
      </span>
    </div>
  );
}
